import { useEffect, useId, useRef, type ReactNode } from "react";
import { Button } from "../shadcn/Button";

export interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  dismissLabel?: string;
  onConfirm: () => void;
  destructive?: boolean;
  pending?: boolean;
  error?: string;
}

export function ConfirmationDialog({
  open, onOpenChange, title, description, confirmLabel, dismissLabel = "Keep",
  onConfirm, destructive = false, pending = false, error,
}: ConfirmationDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const dismissRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    const previousFocus = document.activeElement;
    dialog.showModal();
    dismissRef.current?.focus();
    return () => {
      dialog.close();
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      aria-busy={pending}
      onCancel={(event) => {
        event.preventDefault();
        if (!pending) onOpenChange(false);
      }}
      className="fixed inset-0 m-auto max-h-[calc(100dvh-32px)] w-[calc(100%-32px)] max-w-lg overflow-y-auto rounded-[20px] border border-border bg-card p-5 text-card-foreground backdrop:bg-black/40"
    >
      <div className="space-y-5">
        <div className="flex flex-col gap-2 text-left">
          <h2 id={titleId} className="text-[21px] font-bold leading-tight">{title}</h2>
          <p id={descriptionId} className="text-sm text-muted-foreground">{description}</p>
        </div>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&>button]:min-h-12">
          <Button ref={dismissRef} variant="outline" disabled={pending} onClick={() => onOpenChange(false)}>{dismissLabel}</Button>
          <Button variant={destructive ? "destructive" : "default"} disabled={pending} onClick={onConfirm}>
            {pending ? "Please wait..." : confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}
